import { STATUS_MAP } from '../../utils/catalogs';
import { FAMILY_ORDER, getChartColor, getStatusFamily } from '../../utils/chartColors';
import { getLastDaysRange } from '../../utils/dates';
import { ACTIVITY_TONE_BY_STATUS, DASHBOARD_DEFAULT_DAYS } from './dashboardConstants';

/** The window the dashboard opens on. */
export const getDefaultRange = () => getLastDaysRange(DASHBOARD_DEFAULT_DAYS);

/**
 * The stored filters, as they should come back.
 *
 * Everything survives a reload except a window nobody picked. The default one
 * is anchored to today, so restored literally it would age: a week later the
 * dashboard would open on a window that closed days ago and show an empty tower
 * to someone who never touched the picker — which reads as an outage rather
 * than as a filter.
 *
 * A range the user did pick comes back exactly as they left it. There are no
 * presets in this screen, only explicit dates, and "September 1st to 5th" is an
 * answer to a question, not a stale default.
 *
 * @param {object|undefined} stored - The `filters` object read from disk.
 * @returns {object|undefined}
 */
export const restoreFilters = (stored) => {
  if (!stored || typeof stored !== 'object') return stored;
  if (stored.isDefaultRange === false) return stored;

  return { ...stored, ...getDefaultRange(), isDefaultRange: true };
};

/**
 * `1.234` — the thousands separator this product uses everywhere.
 *
 * @param {number|null|undefined} value
 */
export const formatNumber = (value) =>
  typeof value === 'number' ? value.toLocaleString('de-DE') : '0';

/**
 * `94,56%`.
 *
 * @param {number|null|undefined} value - Already a percentage, not a fraction.
 */
export const formatPercent = (value) =>
  `${(typeof value === 'number' ? value : 0).toLocaleString('es-CL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;

/**
 * "Hace 5 minutos".
 *
 * @param {string} value - ISO timestamp
 */
export const formatRelativeTime = (value) => {
  if (!value) return '';

  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';

  const minutes = Math.round((Date.now() - then) / 60000);

  if (minutes < 1) return 'Recién';
  if (minutes === 1) return 'Hace 1 minuto';
  if (minutes < 60) return `Hace ${minutes} minutos`;

  const hours = Math.round(minutes / 60);
  if (hours === 1) return 'Hace 1 hora';
  if (hours < 24) return `Hace ${hours} horas`;

  const days = Math.round(hours / 24);
  return days === 1 ? 'Hace 1 día' : `Hace ${days} días`;
};

/**
 * Turns one activity row into the sentence the feed shows.
 *
 * The backend sends the pieces — order, status, commune — rather than a
 * finished phrase, which is what lets this read "entregada en Pudahuel" while
 * the same row can be rendered differently elsewhere.
 *
 * @param {{order_id: number, status: string, commune: string}} entry
 * @returns {{text: string, tone: string, label: string}}
 */
export const describeActivity = (entry) => {
  const label = STATUS_MAP[entry.status]?.user_name || entry.status || 'Actualizada';
  const tone = ACTIVITY_TONE_BY_STATUS[entry.status] || 'info';
  const where = entry.commune ? ` en ${entry.commune}` : '';

  return {
    tone,
    label,
    text: `Orden #${entry.order_id} · ${label}${where}`,
  };
};

/**
 * The trend line under a KPI: how this window compares with the one before it.
 *
 * Returns `null` when there is nothing to compare against — a first period, or
 * an account that had no shipments back then. Showing "↑ 100%" because the
 * previous window was empty would be noise, not information.
 *
 * @param {number} current
 * @param {number} previous
 * @param {string} suffix - What the number is about, e.g. 'vs periodo anterior'
 */
export const buildTrend = (current, previous, suffix) => {
  if (typeof current !== 'number' || typeof previous !== 'number' || previous === 0) {
    return null;
  }

  const change = ((current - previous) / previous) * 100;
  const rounded = Math.round(change * 10) / 10;

  if (rounded === 0) return { trend: 'neutral', trendValue: `Sin cambios ${suffix}` };

  return {
    trend: rounded > 0 ? 'up' : 'down',
    trendValue: `${rounded > 0 ? '↑' : '↓'} ${Math.abs(rounded).toLocaleString('es-CL')}% ${suffix}`,
  };
};

/**
 * The same, for a value that is already a rate.
 *
 * Rates move in **percentage points**, not in percent. A success rate going
 * from 93,02% to 97,33% is +4,31 pts; calling it "+4,6%" — the relative change
 * — is a different, smaller-sounding number that reads as if the rate itself
 * were 4,6. Both are arithmetically true and only one is what anyone means.
 *
 * @param {number} current - A percentage, e.g. 97.33
 * @param {number} previous
 * @param {string} suffix
 */
export const buildRateTrend = (current, previous, suffix) => {
  if (typeof current !== 'number' || typeof previous !== 'number') return null;

  const points = Math.round((current - previous) * 100) / 100;

  if (points === 0) return { trend: 'neutral', trendValue: `Sin cambios ${suffix}` };

  return {
    trend: points > 0 ? 'up' : 'down',
    trendValue: `${points > 0 ? '↑' : '↓'} ${Math.abs(points).toLocaleString('es-CL')} pts ${suffix}`,
  };
};

/**
 * The donut's slices.
 *
 * Labels from `STATUS_MAP` — they are the product's words for each state —
 * and colours from `chartColors`, which gives every status its own. Reading
 * both from the catalog is what made the donut a grey ring: eight of the
 * nineteen statuses share `#D3D3D3` there.
 *
 * @param {Array<{value: string, count: number}>} statusCount
 */
export const buildDonutData = (statusCount = []) =>
  statusCount.map((item) => ({
    name: STATUS_MAP[item.value]?.user_name || item.value,
    value: item.count,
    color: getChartColor(item.value),
  }));

/**
 * Every date in a range, as `YYYY-MM-DD`.
 *
 * Walked in UTC on purpose: the arithmetic only has to advance one calendar day
 * at a time, and doing it in local time means a DST change either repeats a day
 * or skips one.
 *
 * @param {string} start - `YYYY-MM-DD`
 * @param {string} end - `YYYY-MM-DD`
 * @returns {string[]}
 */
const eachDay = (start, end) => {
  const days = [];
  const cursor = new Date(`${start}T00:00:00Z`);
  const last = new Date(`${end}T00:00:00Z`);

  if (Number.isNaN(cursor.getTime()) || Number.isNaN(last.getTime())) return days;

  while (cursor <= last) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return days;
};

/**
 * The daily series, one row per day, with a column per state family.
 *
 * Recharts stacks by key, so each family has to be its own property rather than
 * a nested array.
 *
 * **The gaps are filled here.** A `GROUP BY` only emits days that have rows, so
 * a month with five busy days comes back as five records — and a bar chart fed
 * those draws five evenly spaced bars, which reads as five consecutive days.
 * A quiet Sunday has to look quiet, not disappear.
 *
 * @param {Array<{date: string, count: number, statuses: Array<{value: string, count: number}>}>} dailyCount
 * @param {{start_date: string, end_date: string}|null} [range] - Without it,
 *   only the days that came back are drawn.
 */
export const buildDailyData = (dailyCount = [], range = null) => {
  const byDate = new Map();

  dailyCount.forEach((day) => {
    const row = { date: day.date, total: day.count };
    FAMILY_ORDER.forEach((family) => { row[family] = 0; });

    day.statuses?.forEach((entry) => {
      row[getStatusFamily(entry.value)] += entry.count;
    });

    byDate.set(day.date, row);
  });

  const emptyRow = (date) => {
    const row = { date, total: 0 };
    FAMILY_ORDER.forEach((family) => { row[family] = 0; });
    return row;
  };

  const dates = range?.start_date && range?.end_date
    ? eachDay(range.start_date, range.end_date)
    : [...byDate.keys()].sort();

  return dates.map((date) => byDate.get(date) || emptyRow(date));
};

/**
 * `05/09`, for a chart axis where the year is already implied by the filter.
 *
 * Built from the string's own parts rather than through `new Date(...)`: an
 * ISO date without a time is parsed as UTC, which in Chile renders the previous
 * day.
 *
 * @param {string} value - `YYYY-MM-DD`
 */
export const formatDayLabel = (value) => {
  if (typeof value !== 'string') return '';
  const [, month, day] = value.split('-');
  return month && day ? `${day}/${month}` : value;
};
